import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ArrowUp, ArrowDown, Users, Medal, MessageSquare, Image, Briefcase, ExternalLink, CheckCircle2 } from "lucide-react";
import { api, fileUrl } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { ImageUpload } from "@/pages/admin/AdminUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// ---------- Team ----------
function Team() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", photo_url: null });
  const load = async () => setItems((await api.get("/team")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    if (form.id) await api.put(`/admin/team/${form.id}`, form); else await api.post("/admin/team", form);
    setOpen(false); setForm({ name: "", title: "", photo_url: null }); load(); toast.success("Saved");
  };
  const del = async (id) => { await api.delete(`/admin/team/${id}`); load(); };
  const move = async (i, d) => { const ids = items.map((x) => x.id); const j = i + d; if (j < 0 || j >= ids.length) return; [ids[i], ids[j]] = [ids[j], ids[i]]; await api.post("/admin/team/reorder", { ids }); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm({ name: "", title: "", photo_url: null }); setOpen(true); }} data-testid="add-team"><Plus className="w-4 h-4 mr-1.5" /> Add Member</Button></div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={Users} title="No team members yet" testId="team-empty" /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((t, i) => (
            <div key={t.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center gap-3" data-testid={`adm-team-${t.id}`}>
              {t.photo_url ? <img src={fileUrl(t.photo_url)} className="w-12 h-12 rounded-xl object-cover" alt="" /> : <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center font-bold">{t.name?.[0]}</div>}
              <div className="flex-1 min-w-0"><p className="font-medium truncate">{t.name}</p><p className="text-xs text-muted-foreground truncate">{t.title}</p></div>
              <div className="flex gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => { setForm(t); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" /></div>
            <div><Label>Role / Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="team-title" /></div>
            <ImageUpload label="Photo" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testId="team-photo" />
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-team">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Leaderboard ----------
function Leaderboard() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_name: "", achievement: "", rate: "", photo_url: null, published: true });
  const load = async () => setItems((await api.get("/admin/leaderboard")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.student_name || !form.achievement) return toast.error("Name and achievement required");
    if (form.id) await api.put(`/admin/leaderboard/${form.id}`, form); else await api.post("/admin/leaderboard", form);
    setOpen(false); setForm({ student_name: "", achievement: "", rate: "", photo_url: null, published: true }); load(); toast.success("Saved");
  };
  const del = async (id) => { await api.delete(`/admin/leaderboard/${id}`); load(); };
  const approve = async (id) => { await api.put(`/admin/leaderboard/${id}`, { action: "approve" }); load(); toast.success("Approved"); };
  const toggle = async (id, published) => { await api.put(`/admin/leaderboard/${id}`, { published }); load(); };
  const move = async (i, d) => { const ids = items.map((x) => x.id); const j = i + d; if (j < 0 || j >= ids.length) return; [ids[i], ids[j]] = [ids[j], ids[i]]; await api.post("/admin/leaderboard/reorder", { ids }); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm({ student_name: "", achievement: "", rate: "", published: true }); setOpen(true); }} data-testid="add-leader"><Plus className="w-4 h-4 mr-1.5" /> Add Entry</Button></div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={Medal} title="No leaderboard entries" testId="leader-empty" /> : (
        <div className="space-y-2">
          {items.map((l, i) => (
            <div key={l.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center gap-3" data-testid={`adm-leader-${l.id}`}>
              <span className="w-8 h-8 rounded-lg bg-orange/10 text-orange flex items-center justify-center font-bold text-sm">{i + 1}</span>
              <div className="flex-1 min-w-0"><p className="font-medium truncate">{l.student_name} {l.rate && <span className="text-brandgreen text-sm">· {l.rate}</span>}</p><p className="text-xs text-muted-foreground truncate">{l.achievement}</p></div>
              {l.status === "pending" && <Button size="sm" className="rounded-full bg-brandgreen hover:bg-brandgreen/90 h-8" onClick={() => approve(l.id)} data-testid={`approve-leader-${l.id}`}>Approve</Button>}
              <label className="flex items-center gap-1 text-xs"><Switch checked={l.published} onCheckedChange={(v) => toggle(l.id, v)} data-testid={`pub-leader-${l.id}`} /></label>
              <div className="flex gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(l.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Leaderboard Entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Student Name</Label><Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="leader-name" /></div>
            <div><Label>Achievement</Label><Input value={form.achievement} onChange={(e) => setForm({ ...form, achievement: e.target.value })} placeholder="Landed 3 clients in a week" data-testid="leader-achievement" /></div>
            <div><Label>Rate (optional)</Label><Input value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="$30/hr" data-testid="leader-rate" /></div>
            <ImageUpload label="Photo (optional)" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testId="leader-photo" />
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="leader-published" /> Published</label>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-leader">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Testimonials ----------
function Testimonials() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const load = async () => setItems((await api.get(`/admin/testimonials?status=${status}`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const act = async (id, action) => { await api.patch(`/admin/testimonials/${id}`, { action }); load(); toast.success("Updated"); };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["all", "pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${status === s ? "bg-teal text-white" : "bg-white border border-border"}`} data-testid={`tst-filter-${s}`}>{s}</button>
        ))}
      </div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={MessageSquare} title="No feedback" testId="tst-admin-empty" /> : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm" data-testid={`adm-tst-${t.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div><p className="font-medium">{t.name} <span className="text-xs text-muted-foreground">{t.role}</span></p><p className="text-xs text-warning">{"★".repeat(t.rating || 0)}</p></div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={t.status === "approved" ? (t.published ? "approved" : "pending") : t.status} />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">"{t.feedback}"</p>
              <div className="flex gap-2 mt-3">
                {t.status !== "approved" && <Button size="sm" className="rounded-full bg-brandgreen hover:bg-brandgreen/90 h-8" onClick={() => act(t.id, "approve")} data-testid={`tst-approve-${t.id}`}>Approve & Publish</Button>}
                {t.status === "approved" && t.published && <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => act(t.id, "unpublish")} data-testid={`tst-unpub-${t.id}`}>Unpublish</Button>}
                {t.status === "approved" && !t.published && <Button size="sm" className="rounded-full bg-brandgreen hover:bg-brandgreen/90 h-8" onClick={() => act(t.id, "publish")}>Publish</Button>}
                {t.status !== "rejected" && <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => act(t.id, "reject")} data-testid={`tst-reject-${t.id}`}>Reject</Button>}
                <Button size="sm" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Banners ----------
function Banners() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ image_url: null, title: "", subtitle: "", link: "" });
  const load = async () => setItems((await api.get("/banners")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.image_url) return toast.error("Please upload an image");
    if (form.id) await api.put(`/admin/banners/${form.id}`, form); else await api.post("/admin/banners", form);
    setOpen(false); setForm({ image_url: null, title: "", subtitle: "", link: "" }); load(); toast.success("Saved");
  };
  const del = async (id) => { await api.delete(`/admin/banners/${id}`); load(); };
  const move = async (i, d) => { const ids = items.map((x) => x.id); const j = i + d; if (j < 0 || j >= ids.length) return; [ids[i], ids[j]] = [ids[j], ids[i]]; await api.post("/admin/banners/reorder", { ids }); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm({ image_url: null, title: "", subtitle: "", link: "" }); setOpen(true); }} data-testid="add-banner"><Plus className="w-4 h-4 mr-1.5" /> Add Slide</Button></div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={Image} title="No banner slides" description="Upload slides for the homepage carousel." testId="banner-empty" /> : (
        <div className="space-y-3">
          {items.map((b, i) => (
            <div key={b.id} className="bg-white border border-border rounded-2xl p-3 shadow-sm flex items-center gap-3" data-testid={`adm-banner-${b.id}`}>
              <img src={b.image_url?.startsWith("http") ? b.image_url : fileUrl(b.image_url)} className="w-24 h-14 rounded-lg object-cover" alt="" />
              <div className="flex-1 min-w-0"><p className="font-medium truncate">{b.title || "(no title)"}</p><p className="text-xs text-muted-foreground truncate">{b.subtitle}</p></div>
              <div className="flex gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => { setForm(b); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(b.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Banner Slide</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <ImageUpload label="Slide image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="banner-image" />
            <div><Label>Title (optional)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="banner-title" /></div>
            <div><Label>Subtitle (optional)</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="banner-subtitle" /></div>
            <div><Label>Link URL (optional)</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="banner-link" /></div>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-banner">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Internal Hiring ----------
function Hiring() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", location: "", type: "", apply_url: "", published: true });
  const load = async () => setItems((await api.get("/admin/internal-jobs")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (form.id) await api.put(`/admin/internal-jobs/${form.id}`, form); else await api.post("/admin/internal-jobs", form);
    setOpen(false); setForm({ title: "", description: "", location: "", type: "", apply_url: "", published: true }); load(); toast.success("Saved");
  };
  const del = async (id) => { await api.delete(`/admin/internal-jobs/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm({ title: "", description: "", location: "", type: "", apply_url: "", published: true }); setOpen(true); }} data-testid="add-internal-job"><Plus className="w-4 h-4 mr-1.5" /> Add Opening</Button></div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={Briefcase} title="No internal openings" description="Post roles for your own company (separate from the student Job Board)." testId="hiring-empty" /> : (
        <div className="space-y-3">
          {items.map((j) => (
            <div key={j.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3" data-testid={`adm-internal-${j.id}`}>
              <div><p className="font-medium">{j.title}</p><p className="text-xs text-muted-foreground">{j.type} {j.location && `· ${j.location}`}</p></div>
              <div className="flex items-center gap-2">
                <StatusBadge status={j.published ? "approved" : "rejected"} />
                <Button size="sm" variant="ghost" onClick={() => { setForm(j); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(j.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Job Opening</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Customer Service Representative" data-testid="ij-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Full-time" data-testid="ij-type" /></div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Remote" data-testid="ij-location" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ij-desc" /></div>
            <div><Label>Apply URL (optional)</Label><Input value={form.apply_url} onChange={(e) => setForm({ ...form, apply_url: e.target.value })} data-testid="ij-url" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="ij-published" /> Published</label>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-internal-job">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminCommunity() {
  return (
    <div>
      <PageHeader title="Community & Homepage" subtitle="Team, leaderboard, testimonials, banners, hiring, and challenges" />
      <Tabs defaultValue="banners">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="banners" data-testid="ctab-banners"><Image className="w-4 h-4 mr-1.5" /> Banners</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="ctab-testimonials"><MessageSquare className="w-4 h-4 mr-1.5" /> Testimonials</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="ctab-leaderboard"><Medal className="w-4 h-4 mr-1.5" /> Leaderboard</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="ctab-challenges"><Medal className="w-4 h-4 mr-1.5" /> Challenges</TabsTrigger>
          <TabsTrigger value="team" data-testid="ctab-team"><Users className="w-4 h-4 mr-1.5" /> Team</TabsTrigger>
          <TabsTrigger value="hiring" data-testid="ctab-hiring"><Briefcase className="w-4 h-4 mr-1.5" /> Hiring</TabsTrigger>
        </TabsList>
        <TabsContent value="banners"><Banners /></TabsContent>
        <TabsContent value="testimonials"><Testimonials /></TabsContent>
        <TabsContent value="leaderboard"><Leaderboard /></TabsContent>
        <TabsContent value="challenges"><Challenges /></TabsContent>
        <TabsContent value="team"><Team /></TabsContent>
        <TabsContent value="hiring"><Hiring /></TabsContent>
      </Tabs>
    </div>
  );
}

function Challenges() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", prize: "", criteria: "", ends_at: "", active: true });
  const load = async () => setItems((await api.get("/admin/challenges")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (form.id) await api.put(`/admin/challenges/${form.id}`, form); else await api.post("/admin/challenges", form);
    setOpen(false); setForm({ title: "", prize: "", criteria: "", ends_at: "", active: true }); load(); toast.success("Saved");
  };
  const del = async (id) => { await api.delete(`/admin/challenges/${id}`); load(); };
  const end = async (id) => { await api.put(`/admin/challenges/${id}`, { active: false }); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm({ title: "", prize: "", criteria: "", ends_at: "", active: true }); setOpen(true); }} data-testid="add-challenge"><Plus className="w-4 h-4 mr-1.5" /> New Challenge</Button></div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={Medal} title="No challenges" description="Create a time-limited contest tied to the leaderboard." testId="challenges-empty" /> : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3" data-testid={`adm-challenge-${c.id}`}>
              <div><p className="font-medium">{c.title} {c.active && <span className="text-xs text-brandgreen">● Active</span>}</p><p className="text-xs text-muted-foreground">{c.prize} {c.ends_at && `· ends ${c.ends_at}`}</p></div>
              <div className="flex gap-1">
                {c.active && <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => end(c.id)} data-testid={`end-challenge-${c.id}`}>End</Button>}
                <Button size="sm" variant="ghost" onClick={() => { setForm(c); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "New"} Challenge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Land a client this week!" data-testid="ch-title" /></div>
            <div><Label>Prize</Label><Input value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} placeholder="₱5,000" data-testid="ch-prize" /></div>
            <div><Label>Eligibility criteria</Label><Textarea value={form.criteria} onChange={(e) => setForm({ ...form, criteria: e.target.value })} placeholder="Must have registered within the last 2 weeks" data-testid="ch-criteria" /></div>
            <div><Label>Ends (text)</Label><Input value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} placeholder="Sunday 11:59 PM" data-testid="ch-ends" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="ch-active" /> Active (shown on homepage — only one active at a time)</label>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-challenge">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
